import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetAiReportsConfigQuery,
  useVerifyAiReportsAccessCodeMutation,
  useGetAiReportsMutation,
  useGetAiReportMutation,
  useDeleteAiReportMutation,
} from '#services/ai-reports';
import AiReportViewer from './AiReportViewer';
import type { AiReport } from './AiReportViewer';

interface AiReportsConfig {
  enabled?: boolean;
  [key: string]: unknown;
}

interface VerifyResult {
  locked?: boolean;
  unlocked?: boolean;
  message?: string;
  attemptsRemaining?: number;
  [key: string]: unknown;
}

interface ReportsListResult {
  reports?: AiReport[];
  [key: string]: unknown;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export default function AiReportsPage() {
  const { data: configData, isLoading: configLoading } = useGetAiReportsConfigQuery(undefined);
  const config = configData as AiReportsConfig | undefined;

  const [unlocked, setUnlocked] = useState(false);
  const [reports, setReports] = useState<AiReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<AiReport | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [lockout, setLockout] = useState<VerifyResult | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const [verifyCode, { isLoading: verifying }] = useVerifyAiReportsAccessCodeMutation();
  const [fetchReports, { isLoading: loadingReports }] = useGetAiReportsMutation();
  const [fetchReport] = useGetAiReportMutation();
  const [deleteReport, { isLoading: deleting }] = useDeleteAiReportMutation();

  const initDoneRef = useRef(false);

  // Returns true on success, false on failure (caller can decide how to surface the error).
  const loadReports = async () => {
    const result = await fetchReports(undefined);
    const data = 'data' in result ? result.data as ReportsListResult | undefined : undefined;
    if ('error' in result || !data) {
      setError('Failed to load reports. The session may have expired — please refresh the page.');
      return false;
    }
    setError('');
    setReports(data.reports || []);
    return true;
  };

  // On mount, probe the server to check if the session is already unlocked.
  // This satisfies FR-006: once unlocked, the session persists without re-entry.
  useEffect(() => {
    if (!config?.enabled || initDoneRef.current) return;
    initDoneRef.current = true;
    void fetchReports(undefined).then((result) => {
      const data = 'data' in result ? result.data as ReportsListResult | undefined : undefined;
      if (data && data.reports) {
        setUnlocked(true);
        setReports(data.reports);
      }
    });
  }, [config]);

  if (configLoading) {
    return <CircularProgress />;
  }

  if (!config || !config.enabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>AI Reports</Typography>
        <Alert severity="info">This feature is not currently enabled.</Alert>
      </Box>
    );
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLockout(null);

    const result = await verifyCode({ accessCode });
    const data = 'data' in result ? result.data as VerifyResult | undefined : undefined;

    if (!data) {
      setError('Failed to verify access code. Please try again.');
      return;
    }

    if (data.locked) {
      setLockout(data);
      return;
    }

    if (data.unlocked) {
      setAccessCode('');
      // Mark session as unlocked first, then load the list.
      // If loading fails the error is surfaced in the list view, not the gate form.
      setUnlocked(true);
      await loadReports();
    } else {
      const remaining = data.attemptsRemaining;
      setError(`Invalid access code. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
    }
  };

  const handleSelectReport = async (reportId: string) => {
    const result = await fetchReport({ reportId });
    const data = 'data' in result ? result.data as AiReport | undefined : undefined;
    if ('error' in result || !data) {
      setError('Failed to load report. Please try again.');
      return;
    }
    setError('');
    setSelectedReport(data);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    const result = await deleteReport({ reportId: deleteConfirmId });
    setDeleteConfirmId(null);
    if ('error' in result) {
      setError('Failed to delete report. Please try again.');
      return;
    }
    const data = result.data as { deleted?: boolean } | undefined;
    if (data && data.deleted) {
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
      await loadReports();
    }
  };

  if (!unlocked) {
    return (
      <Box sx={{ p: 3, maxWidth: 400 }}>
        <Typography variant="h5" gutterBottom>AI Reports</Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Enter the access code to view AI reports.
        </Typography>
        {lockout ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {lockout.message}
          </Alert>
        ) : (
          <Box component="form" onSubmit={(e) => { void handleVerify(e); }}>
            <TextField
              label="Access Code"
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoComplete="off"
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={verifying || !accessCode}
              fullWidth
            >
              {verifying ? <CircularProgress size={20} /> : 'Unlock'}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (selectedReport) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setSelectedReport(null)}
          sx={{ mb: 2 }}
        >
          Back to Reports
        </Button>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <AiReportViewer report={selectedReport} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5">AI Reports</Typography>
        <Chip label="Experimental AI" size="small" color="warning" variant="outlined" />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
          maxWidth: 680
        }}>
        AI Reports bring AI-powered insights into the sales workflow — from sales forecasts
        and inventory optimizations to performance trends. External AI agents analyze and
        upload their findings here, giving the team a single place to review actionable
        recommendations without leaving the dashboard.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Reports are automatically deleted after 2 years.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>Report deleted successfully.</Alert>
      )}
      {loadingReports ? (
        <CircularProgress />
      ) : reports.length === 0 && !error ? (
        <Typography sx={{
          color: "text.secondary"
        }}>No reports have been uploaded yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Size</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow
                key={String(report._id)}
                hover
                sx={{ cursor: 'pointer' }}
              >
                <TableCell
                  onClick={() => { void handleSelectReport(String(report._id)); }}
                  sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {report.title}
                </TableCell>
                <TableCell
                  onClick={() => { void handleSelectReport(String(report._id)); }}
                >
                  {new Date(report.createdAt as string | number | Date).toLocaleString()}
                </TableCell>
                <TableCell
                  onClick={() => { void handleSelectReport(String(report._id)); }}
                >
                  {report.sourceUser && report.sourceUser.username}
                </TableCell>
                <TableCell
                  onClick={() => { void handleSelectReport(String(report._id)); }}
                >
                  {formatFileSize(report.fileSize || 0)}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmId(String(report._id));
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this report? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => { void handleDeleteConfirm(); }}
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
